/**
 * Everything the shell knows that the server does not.
 *
 * Which conversation is open, which filter chip is lit, whether the desk is
 * out, which overlay is up — none of that is anybody's data, so none of it is
 * in the query cache and none of it is in the address bar. It is plain React
 * state in one context, which is the architecture rule this app already keeps:
 * TanStack Query owns server state, React owns the rest.
 *
 * WHY ONE OBJECT RATHER THAN A DOZEN `useState`s SPREAD ABOUT. The Escape key.
 * Escape has to close the frontmost thing and nothing else, and "frontmost" is
 * a fact about the whole shell — a dialog over a sheet over a panel over the
 * rail. Written as separate states it becomes five components each guessing
 * whether it is on top, and the fifth one always guesses wrong. Here the
 * cascade is nine lines in one function that can see the whole stack.
 *
 * There is no addressable state here on purpose. A conversation is not a page:
 * the old `/knocks/:id` address was a bookmark to a moment, and this shell has
 * one address (`/`) with a list beside it. The one thin route that survives
 * (`/you/plan`) exists because Stripe has to come back somewhere, and it does
 * exactly one thing — open a panel and get out of the way. It had a sibling for
 * the other checkout, and ruling R-0916-1 deleted that checkout.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  COMPOSE_ID,
  DEFAULT_FILTER,
  OWN_ID,
  type FilterId,
  type SortId,
} from "../../lib/conversations";
import { clearKnockTarget, loadKnockBody, saveKnockTarget } from "../../lib/draft";

/** Below this the shell is two panes deep instead of three. */
export const DESKTOP = "(min-width: 768px)";

/**
 * Is there room for three columns right now?
 *
 * ONE definition, asked two ways: `AppShell` subscribes to it through
 * `useSyncExternalStore` so the layout re-renders when the window crosses the
 * breakpoint, and this provider reads it once, at birth, to decide whether the
 * desk starts out. Both go through this function and this query string, so the
 * seed and the layout cannot disagree about what "desktop" means — a second
 * copy of the number is exactly how they would.
 *
 * No viewport (a server render) answers `true`, which is the same guess
 * `useDesktop`'s SSR snapshot makes: it draws every pane, so nothing is missing
 * before the first real measurement.
 */
export function isDesktop(): boolean {
  return typeof window === "undefined" ? true : window.matchMedia(DESKTOP).matches;
}

/** The pages the settings panel can stand on. `home` is its own index. */
export type SettingsPage = "home" | "look" | "door" | "account" | "help";

/**
 * The views the plan panel can stand on. `home` is where you stand, `plans` is
 * the ladder, `invoices` is the invoices and receipts (wave 87). There was a
 * `credits` view, which was the wallet; ruling R-0916-1 deleted it, and the
 * union is kept closed because a view nothing can render is a route into a
 * blank panel.
 */
export type BillingView = "home" | "plans" | "invoices";

/**
 * Which block of a settings page the reader was actually sent to.
 *
 * A page is not always a destination: "Personalize your house rules" means the
 * rules editor, and the rules editor is the fifth block down a long page (owner
 * item 8, 2026-08-27). The panel carries the intent so the page can bring that
 * block into view and put focus on its first control — otherwise a CTA that
 * names one thing lands somebody at the top of a page that begins with another.
 *
 * It is deliberately a small closed set, not a selector string: a caller that
 * could ask for any node would be reaching into a page it does not own.
 */
/**
 * `public-line`: the rail's Edit beside "What gets through" (ruling R-0925-2
 * (5)). That block prints the door's public line, so its Edit opens the field
 * the line is written in — not the House Rules, which are private.
 */
export type SettingsFocus = "rules" | "public-line";

/**
 * The same thing for the plan panel (wave 57). "Get more usage", pressed in a
 * conversation that met the wall, means the options — the rung above on a
 * spent paid plan, or the ladder — and not the top of a view that begins with
 * a paragraph about what a plan is for.
 */
export type BillingFocus = "options" | "trial";

export type Overlay =
  | { kind: "none" }
  | {
      kind: "settings";
      page: SettingsPage;
      focus?: SettingsFocus;
    }
  | { kind: "billing"; view: BillingView; focus?: BillingFocus };

/**
 * A sheet is a small thing over the shell; a panel is a whole side of it.
 *
 * `reopen` is the one question the word "Paused" asks, from any of the four
 * places it is printed (`you/reopen.tsx`). It is shell state rather than four
 * local dialogs so that it joins the escape cascade below exactly once.
 */
export type SheetKind = "none" | "share" | "offline" | "knock" | "reopen";

/**
 * A line put in the doorman's composer by something that is not the composer.
 *
 * The nonce is what makes it work twice. Asking the same thing again is a
 * legitimate act, and a bare string would be `===` to the last one and land as
 * no change at all.
 *
 * `send` is the difference between offering a sentence and saying it. A button
 * whose whole label is the request — "Personalize your house rules", "Ask Nob
 * to write you one" — has already been consented to by being pressed, and
 * dropping its words into a box for the owner to press Enter on is a second
 * confirmation of the same decision. A button that merely opens a subject
 * still drafts.
 */
export type PendingAsk = {
  text: string;
  nonce: number;
  send: boolean;
  /**
   * The card the owner's next sentence revises (ruling R-0915-1 (4)). Set by
   * Adjust in "Nob has noticed", which has nothing to say on the owner's behalf
   * — so the text is empty and the composer simply opens, pointed at the card.
   */
  adjustOf?: string;
};

/**
 * A knock being WRITTEN — the whole of it, because the server has none of it.
 *
 * A SIGNED-IN PERSON NEVER SEES THE DOOR POSTER (owner, 2026-08-26). The at-door
 * flow is a trick for a stranger: somebody who arrived cold at `nob.me/@maya`
 * has to be told what a door is before they will write anything, and the poster
 * is how they are told. A person who is already inside the product has been told
 * — so `/@maya` and the `+` dialog both open a conversation here instead, and
 * knocking becomes what it looks like: typing into a thread and pressing send.
 *
 * `draft` LIVES HERE AND NOT IN THE PANE, which is the whole reason this is
 * shell state at all. The pane unmounts the moment somebody opens another
 * conversation to check something, and a draft in the pane's own `useState`
 * would go with it. It is NOT in `lib/draft.ts` either: that machinery exists
 * because Stripe and the identity provider LEAVE THE ORIGIN and a round trip
 * has to survive them, and nothing here leaves the origin. Plain React state,
 * which is this app's rule for everything that is not the server's.
 *
 * `knockId` is what the thread becomes. Once the send succeeds the row is a real
 * knock and the hearing runs in the same pane — the composer's words are handed
 * to it as a draft, exactly as `pre-auth.tsx`'s held words are, and are still
 * nobody's to send but the sender's.
 */
export type Compose = {
  handle: string;
  draft: string;
  knockId: string | null;
  /** When this was opened. The row's timestamp, and it is not a guess. */
  at: number;
};

export type ShellUi = {
  activeId: string | null;
  pendingAsk: PendingAsk | null;
  compose: Compose | null;
  search: string;
  filter: FilterId;
  sort: SortId;
  rightOpen: boolean;
  mobilePane: "list" | "chat";
  overlay: Overlay;
  sheet: SheetKind;
  /**
   * The §5.4 offer is up. One opener is left — "Tell Nob" on Free
   * (`rules-panel.tsx`) — so there is no reason to carry (wave 61).
   */
  paywall: boolean;

  setActive: (id: string | null) => void;
  /**
   * Open a conversation WITHOUT taking a phone into it.
   *
   * The shell lands on the owner's own thread, because that is where the
   * briefing is and where a door owner's day starts. On a desktop that fills
   * the middle pane and the list is still beside it; on a phone, sliding
   * straight into a conversation nobody asked for would hide the list somebody
   * opened the app to read.
   */
  preselect: (id: string) => void;
  setSearch: (value: string) => void;
  setFilter: (id: FilterId) => void;
  setSort: (id: SortId) => void;
  toggleRail: () => void;
  setRightOpen: (open: boolean) => void;
  setMobilePane: (pane: "list" | "chat") => void;
  openSettings: (page?: SettingsPage, focus?: SettingsFocus) => void;
  /**
   * Adjust, from the rules queue: the owner's own thread, composer focused, the
   * card named (ruling R-0915-1 (4)). It used to open the markdown editor on
   * the proposed document; the ruling reverses that, and the editor is reached
   * from Settings and nowhere else.
   */
  adjustProposal: (proposalId: string) => void;
  openBilling: (view?: BillingView, focus?: BillingFocus) => void;
  closeOverlay: () => void;
  openSheet: (sheet: Exclude<SheetKind, "none">) => void;
  closeSheet: () => void;
  openPaywall: () => void;
  closePaywall: () => void;
  /**
   * Open the owner's own thread with these words already typed — or, with
   * `send`, already said.
   */
  askDoorman: (text: string, send?: boolean) => void;
  /**
   * Open a knock at this door, in the console. Replaces whatever was being
   * written — one draft at a time, like the one own-thread — unless it is the
   * same door, in which case the words already typed are left alone.
   *
   * `seed` puts words in the box that this browser did not type: the line
   * somebody wrote when they held a spot at a door that has since opened (owner
   * item 3). It is an OFFER, not a send — the words sit in the composer for
   * them to change or delete — and it loses to a draft already in progress at
   * the same door for exactly the reason the stored draft does.
   */
  openCompose: (handle: string, seed?: string) => void;
  /** What is in the box. Every keystroke, because the box is not the owner. */
  setComposeDraft: (draft: string) => void;
  /**
   * Words that arrived LATE, from the server's copy of a pre-account draft
   * (ruling R-0828-3) — offered rather than set.
   *
   * `setComposeDraft` is the composer speaking about itself and overwrites
   * without asking, which is right for a keystroke and wrong for an answer that
   * came back over the network some hundreds of milliseconds after the thread
   * opened. In that gap somebody may have typed, closed the thread, or pressed
   * send; so this seeds ONLY a thread that is still at that handle, still not a
   * hearing, and still empty. Anything else and the words the person is looking
   * at are newer than the ones that just arrived, and they win.
   */
  seedComposeDraft: (handle: string, body: string) => void;
  /** The send landed: this thread is a knock now, and the hearing is running. */
  composeStarted: (knockId: string) => void;
  /** Nothing left to write. Closes the thread if it is the one on screen. */
  clearCompose: () => void;
  /**
   * Start the door step, or null when there is none owed (§2.3.3, ruling C5).
   *
   * It lives in the shell's context rather than being handed down as a prop
   * because TWO surfaces offer the same act — the pinned row a knocker goes
   * looking for a door in, and the pane behind it — and two implementations of
   * one act is how they come to differ. The step itself is a screen at `/`, so
   * what is carried here is the way to ask for it, never the screen.
   */
  setUpDoor: (() => void) | null;
  /** The cascade. Returns true when it consumed the key. */
  escape: () => boolean;
};

const ShellContext = createContext<ShellUi | null>(null);

export function ShellProvider({
  children,
  initialOverlay,
  onSetUpDoor = null,
}: {
  children: ReactNode;
  /** The door step, when one is owed. See `ShellUi.setUpDoor`. */
  onSetUpDoor?: (() => void) | null;
  /**
   * What a checkout return asks the shell to be showing on arrival. It is an
   * initial value rather than a prop the shell keeps reading, because the
   * person is then free to close it — and a controlled overlay would spring
   * straight back open.
   */
  initialOverlay?: Overlay;
}) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterId>(DEFAULT_FILTER);
  const [sort, setSort] = useState<SortId>("recent");
  /**
   * The desk starts OUT on a desktop and shut on a phone (owner, 2026-08-25).
   *
   * A lazy initialiser rather than a prop threaded down from the layout: the
   * breakpoint is a fact about the window, not about this tree, and passing it
   * in would mean two components deciding it. Read once — a later resize moves
   * the layout, but it must not reach in and reopen a desk somebody closed.
   */
  const [rightOpen, setRightOpen] = useState(isDesktop);
  const [mobilePane, setMobilePane] = useState<"list" | "chat">("list");
  const [overlay, setOverlay] = useState<Overlay>(initialOverlay ?? { kind: "none" });
  const [sheet, setSheet] = useState<SheetKind>("none");
  const [paywall, setPaywall] = useState(false);
  const [pendingAsk, setPendingAsk] = useState<PendingAsk | null>(null);
  const [compose, setCompose] = useState<Compose | null>(null);
  const asks = useRef(0);

  /** Opening a conversation is also the mobile navigation: list → chat. */
  const setActive = useCallback((id: string | null) => {
    setActiveId(id);
    if (id) setMobilePane("chat");
  }, []);

  const preselect = useCallback((id: string) => setActiveId(id), []);

  const openSettings = useCallback((page: SettingsPage = "home", focus?: SettingsFocus) => {
    setOverlay({ kind: "settings", page, ...(focus ? { focus } : {}) });
  }, []);

  /**
   * ADJUST, FROM THE QUEUE (ruling R-0915-1 (4)). It opened the markdown editor
   * on the proposed document until the ruling reversed R-0914-2 (3); adjusting
   * is talking now, so it does what pressing Adjust inside the thread does —
   * the overlay closes, the owner's own conversation comes up, and the composer
   * is pointed at the card. The empty text is the point: Nob does not put words
   * in the owner's mouth about their own rules.
   */
  const adjustProposal = useCallback((proposalId: string) => {
    setOverlay({ kind: "none" });
    asks.current += 1;
    setPendingAsk({ text: "", nonce: asks.current, send: false, adjustOf: proposalId });
    setActiveId(OWN_ID);
    setMobilePane("chat");
  }, []);

  const openBilling = useCallback((view: BillingView = "home", focus?: BillingFocus) => {
    setOverlay(focus ? { kind: "billing", view, focus } : { kind: "billing", view });
  }, []);

  const askDoorman = useCallback((text: string, send = false) => {
    asks.current += 1;
    setPendingAsk({ text, nonce: asks.current, send });
    setActiveId(OWN_ID);
    setMobilePane("chat");
  }, []);

  const openCompose = useCallback((handle: string, seed?: string) => {
    const bare = handle.replace(/^@/, "").toLowerCase();
    // Which door is open is the one thing here worth surviving a reload: a
    // hearing that is running has to be findable again, and this is the key the
    // door page used for the same job. Everything else about a compose thread —
    // the draft, the id — is memory, and memory is what it should be.
    saveKnockTarget(bare);
    setCompose((current) =>
      // The same door twice is not a new draft — a person who taps back to a
      // door they are already writing at expects their words to be there. But
      // an EMPTY thread at that door has no words to protect, and a seed does:
      // the boot re-aims a compose thread at the stored knock target with no
      // draft, and the opened-door shelf's line was being swallowed by exactly
      // this arm (found by the gauntlet's conversion e2e, 2026-08-27).
      current && current.handle === bare && current.knockId === null
        ? seed?.trim() && current.draft.trim() === ""
          ? { ...current, draft: seed.trim() }
          : current
        : // …and neither is the same door after a RELOAD. The draft is React
          // state, which is right for a thread somebody is standing in, and
          // wrong for the one moment this thread does leave the origin: the
          // refusal at the send, and the Stripe trip that answers it. What
          // comes back from `localStorage` here is what was typed before that
          // trip (owner item 28); a door with nothing stored answers "".
          //
          // A `seed` outranks the store because the store is empty in the case
          // it exists for: somebody who queued at a handle typed their line into
          // the waiting room, not into this browser's knock draft, so there is
          // nothing here to lose. The composer persists it on its next effect,
          // which is how the seeded words then survive a reload like any other.
          {
            handle: bare,
            draft: seed?.trim() || loadKnockBody(bare),
            knockId: null,
            at: Date.now(),
          },
    );
    setActiveId(COMPOSE_ID);
    setMobilePane("chat");
  }, []);

  const setComposeDraft = useCallback((draft: string) => {
    setCompose((current) => (current ? { ...current, draft } : current));
  }, []);

  const seedComposeDraft = useCallback((handle: string, body: string) => {
    const bare = handle.replace(/^@/, "").toLowerCase();
    setCompose((current) =>
      current &&
      current.handle === bare &&
      current.knockId === null &&
      current.draft.trim() === "" &&
      body.trim()
        ? { ...current, draft: body }
        : current,
    );
  }, []);

  const composeStarted = useCallback((knockId: string) => {
    setCompose((current) => (current ? { ...current, knockId } : current));
  }, []);

  const clearCompose = useCallback(() => {
    // The stored target goes with it. It is what re-aims a reload at this door
    // (`app-shell.tsx`), so a thread somebody has closed leaving one behind
    // would reopen itself the next time they opened the app.
    clearKnockTarget();
    setCompose(null);
    setActiveId((current) => (current === COMPOSE_ID ? null : current));
  }, []);

  const closeOverlay = useCallback(() => setOverlay({ kind: "none" }), []);
  const openSheet = useCallback((next: Exclude<SheetKind, "none">) => setSheet(next), []);
  const closeSheet = useCallback(() => setSheet("none"), []);
  const openPaywall = useCallback(() => setPaywall(true), []);
  const closePaywall = useCallback(() => setPaywall(false), []);
  const toggleRail = useCallback(() => setRightOpen((open) => !open), []);

  /**
   * Frontmost first, and exactly one thing per press.
   *
   * The order is the stacking order, which is also the order somebody built the
   * stack in: the paywall sits over the panel that offered it, the sheet over
   * the panel that opened it, the panel over the shell, the desk beside the
   * conversation, and the conversation over the list on a phone. Returning
   * `false` is how the key gets handed back to the browser when the shell has
   * nothing of its own to close.
   */
  const escape = useCallback((): boolean => {
    if (paywall) {
      setPaywall(false);
      return true;
    }
    if (sheet !== "none") {
      setSheet("none");
      return true;
    }
    if (overlay.kind !== "none") {
      setOverlay({ kind: "none" });
      return true;
    }
    if (rightOpen) {
      setRightOpen(false);
      return true;
    }
    if (mobilePane === "chat") {
      setMobilePane("list");
      return true;
    }
    return false;
  }, [mobilePane, overlay.kind, paywall, rightOpen, sheet]);

  const value = useMemo<ShellUi>(
    () => ({
      activeId,
      pendingAsk,
      compose,
      search,
      filter,
      sort,
      rightOpen,
      mobilePane,
      overlay,
      sheet,
      paywall,
      setActive,
      preselect,
      setSearch,
      setFilter,
      setSort,
      toggleRail,
      setRightOpen,
      setMobilePane,
      openSettings,
      adjustProposal,
      openBilling,
      closeOverlay,
      openSheet,
      closeSheet,
      openPaywall,
      closePaywall,
      askDoorman,
      openCompose,
      setComposeDraft,
      seedComposeDraft,
      composeStarted,
      clearCompose,
      setUpDoor: onSetUpDoor,
      escape,
    }),
    [
      onSetUpDoor,
      activeId,
      pendingAsk,
      compose,
      search,
      filter,
      sort,
      rightOpen,
      mobilePane,
      overlay,
      sheet,
      paywall,
      setActive,
      preselect,
      toggleRail,
      openSettings,
      adjustProposal,
      openBilling,
      closeOverlay,
      openSheet,
      closeSheet,
      openPaywall,
      closePaywall,
      askDoorman,
      openCompose,
      setComposeDraft,
      seedComposeDraft,
      composeStarted,
      clearCompose,
      escape,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
}

export function useShellUi(): ShellUi {
  const value = useContext(ShellContext);
  if (!value) throw new Error("useShellUi outside the shell");
  return value;
}
